import { TradeTermsLayout } from "~/components/tradeTermsAssignment/TradeTermsLayout";
import { InputText } from "@sellar-io/components";
import { usePromotion } from "~/hooks/api/v2/promotion/usePromotion";
import { useNavigate } from "react-router";
import * as ROUTES from "~/constants/routes";
import { PromotionContext, usePromotionContext } from "./PromotionContext";
import { SpinnerLegacy } from "~/components";
import { usePromotionUpdate } from "~/hooks/api/v2/promotion/usePromotionUpdate";
import { useState, Fragment } from "react";
import { useDebounceValue } from "usehooks-ts";
import dayjs from "dayjs";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { PromotionForm } from "./GlobalPromotionsTab";
import {
  getDatesOverlap,
  PromotionFormSchemaType,
} from "./GlobalPromotionsTab.shared";
import { usePromotionList } from "~/hooks/api/v2/promotion/usePromotionList";
import { PromotionGetResponseType } from "@sellar-io/api-v2/types";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  DataButton,
  SmallButton,
  Icon,
  InputCurrency,
  InputNumber,
  InputContainer,
  Button,
  Card,
  InputIcon,
  InputLabel,
} from "@sellar-io/components";
import z from "zod";
import { usePromotionRewardItemCreate } from "~/hooks/api/v2/promotion-reward-item/usePromotionRewardItemCreate";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "~/hooks/usePriceFormat";
import { useForm } from "@tanstack/react-form";
import { useProductList } from "../../hooks/api/v2/product/useProductList";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "~/components/modal/Dialog";
import { Alert } from "~/components/alert/Alert";
import { useSupplierProduct } from "~/hooks/api/v2/product/useSupplierProduct";
import { ImageWithFallback } from "~/components/image/imageWithFallback";
import { twMerge } from "tailwind-merge";
import { formatCurrency } from "@sellar-io/common";
import { usePromotionRewardItemUpdate } from "~/hooks/api/v2/promotion-reward-item/usePromotionRewardItemUpdate";
import { usePromotionRewardItemDelete } from "~/hooks/api/v2/promotion-reward-item/usePromotionRewardItemDelete";

export const Promotion = ({
  promotionId,
  backRoute = ROUTES.PROMOTIONS,
}: {
  promotionId: number;
  backRoute?: string;
}) => {
  const promotionQuery = usePromotion(promotionId, {
    enabled: !!promotionId,
  });

  if (promotionQuery.isLoading || !promotionQuery.data) {
    return <SpinnerLegacy />;
  }

  return (
    <PromotionContext.Provider value={{ promotion: promotionQuery.data }}>
      <PromotionLayout backRoute={backRoute} />
    </PromotionContext.Provider>
  );
};

const PromotionLayout = ({ backRoute }: { backRoute: string }) => {
  const navigate = useNavigate();
  const { promotion } = usePromotionContext();

  return (
    <TradeTermsLayout
      aside={<PromotionDetailAside />}
      title={promotion.name}
      onClickLeftIcon={() => {
        navigate(backRoute);
      }}
      hideFooter={true}
    >
      <div className="mx-auto flex max-w-230 flex-col gap-2">
        <PromotionContent />
      </div>
    </TradeTermsLayout>
  );
};

const PromotionDetailAside = () => {
  const { business } = useSellarCurrentBusiness();
  const supplierId = business?.Supplier?.id;
  const { promotion } = usePromotionContext();
  const promotionUpdateMutation = usePromotionUpdate(promotion.id);
  const [errorMessage, setErrorMessage] = useState("");
  const [
    overlappingPromotionsByDateCount,
    setOverlappingPromotionsByDateCount,
  ] = useState(0);

  const promotionsQuery = usePromotionList(
    {
      query: supplierId
        ? [{ type: "SupplierId", modifier: "EQUALS", value: supplierId }]
        : [],
    },
    { enabled: Boolean(supplierId) },
  );

  const promotions = promotionsQuery.data?.pages[0] ?? [];

  const initialData = {
    isActive: Boolean(promotion.isActive),
    name: promotion.name,
    startAt: dayjs(promotion.startAt).startOf("day").format("YYYY-MM-DD"),
    endAt: dayjs(promotion.endAt).startOf("day").format("YYYY-MM-DD"),
    rewardRule: promotion.rewardRule,
  };

  const handleUpdatePromotion = (data: PromotionFormSchemaType) => {
    setErrorMessage("");
    promotionUpdateMutation.mutate(
      {
        name: data.name.trim(),
        startAt: dayjs(data.startAt).startOf("day").toDate(),
        endAt: dayjs(data.endAt).startOf("day").toDate(),
        rewardRule: data.rewardRule as PromotionGetResponseType["rewardRule"],
        isActive: data.isActive,
      },
      {
        onSuccess: () => {
          setErrorMessage("");
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleMonitorDateOverlap = ({
    startAt,
    endAt,
  }: {
    startAt: string;
    endAt: string;
  }) => {
    const newStartAt = startAt ? dayjs(startAt).startOf("day").toDate() : null;
    const newEndAt = endAt ? dayjs(endAt).startOf("day").toDate() : null;

    const overlappingPromotions = promotions
      .filter((p) => p.id !== promotion.id)
      .map((p) => ({
        startAt: dayjs(p.startAt).startOf("day").toDate(),
        endAt: dayjs(p.endAt).endOf("day").toDate(),
      }))
      .filter((promotionDates) =>
        getDatesOverlap({
          startDateA: newStartAt,
          endDateA: newEndAt,
          startDateB: promotionDates.startAt,
          endDateB: promotionDates.endAt,
        }),
      );

    setOverlappingPromotionsByDateCount(overlappingPromotions.length);
  };

  return (
    <div className="flex flex-col p-4">
      <PromotionForm
        mode="update"
        initialData={initialData}
        errorMessage={errorMessage}
        metadata={{ overlappingPromotionsByDateCount }}
        isSubmitting={promotionUpdateMutation.isPending}
        onChangeDates={handleMonitorDateOverlap}
        onSubmit={handleUpdatePromotion}
      />
    </div>
  );
};

const PromotionContent = () => {
  const { promotion } = usePromotionContext();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold">Assign rewards</span>
          <span className="text-zinc-500">
            Add the rewards you want customers who qualify for this promotion to
            choose from
          </span>
        </div>
        <AssignPromotionRewardItemButton
          key={promotion.id}
          promotionId={promotion.id}
        />
      </div>
      {promotion.PromotionRewardItems.length === 0 && "No rewards set"}
      {promotion.PromotionRewardItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {promotion.PromotionRewardItems.map((rewardItem) => (
            <PromotionRewardListItem
              key={rewardItem.id}
              rewardItem={rewardItem}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PromotionRewardItemFormSchema = z.object({
  productId: z.number().min(1, { message: "Select a product as a reward" }),
  spendThreshold: z
    .number()
    .min(1, { message: "Select a spend threshold for this reward" }),
  quantity: z.number().min(1, { message: "Set a quantity for this reward" }),
  price: z.number().min(0, { message: "Set a price for this reward" }),
});

type PromotionRewardItemFormSchemaType = z.infer<
  typeof PromotionRewardItemFormSchema
>;

const defaultPromotionRewardItemForm: PromotionRewardItemFormSchemaType = {
  productId: 0,
  spendThreshold: 0,
  quantity: 1,
  price: 0,
};

const AssignPromotionRewardItemButton = ({
  promotionId,
}: {
  promotionId: number;
}) => {
  const [dialogActive, setDialogActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const promotionRewardItemCreateMutation = usePromotionRewardItemCreate();

  const handleDismiss = () => {
    setErrorMessage("");
    setDialogActive(false);
  };

  const handleAssignRewardToPromotion = (
    data: PromotionRewardItemFormSchemaType,
  ) => {
    setErrorMessage("");
    const spendThreshold = formatPriceInputToApi(data.spendThreshold);
    if (!spendThreshold) {
      setErrorMessage("Please set a valid spend threshold");
      return;
    }

    const price = formatPriceInputToApi(data.price);
    if (!price && price !== 0) {
      setErrorMessage("Set a valid price");
      return;
    }

    promotionRewardItemCreateMutation.mutate(
      {
        quantity: data.quantity,
        spendThreshold,
        price,
        ProductId: data.productId,
        PromotionId: promotionId,
      },
      {
        onSuccess: () => handleDismiss(),
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  return (
    <>
      <DataButton
        onClick={() => setDialogActive(true)}
        className="flex items-center gap-1"
        border={true}
      >
        <Icon icon="PLUS" />
        New
      </DataButton>

      <Dialog active={dialogActive} onDismiss={handleDismiss}>
        <DialogNavigation
          title="Assign rewards"
          description="Add the rewards you want customers who qualify for this promotion to choose from "
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={handleDismiss}
        />
        <DialogContent>
          <PromotionRewardItemForm
            mode="create"
            errorMessage={errorMessage}
            initialData={defaultPromotionRewardItemForm}
            isDeleting={false}
            isSubmitting={promotionRewardItemCreateMutation.isPending}
            onSubmit={handleAssignRewardToPromotion}
            onDelete={() => {}}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

type PromotionRewardItemFormProps = {
  mode: "create" | "update";
  initialData: PromotionRewardItemFormSchemaType;
  errorMessage: string;
  isSubmitting: boolean;
  isDeleting: boolean;
  onSubmit: (_formData: PromotionRewardItemFormSchemaType) => void;
  onDelete: () => void;
};

const PromotionRewardItemForm = ({
  initialData,
  isSubmitting,
  isDeleting,
  errorMessage,
  mode,
  onDelete,
  onSubmit,
}: PromotionRewardItemFormProps) => {
  const form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: PromotionRewardItemFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(PromotionRewardItemFormSchema.parse(value));
    },
  });

  const isFormDisabled = isSubmitting || isDeleting;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <form.Field name="productId">
        {({ state, handleChange }) => {
          return (
            <div>
              <PromotionRewardItemSelect
                disabled={isFormDisabled}
                onChange={(productId) => handleChange(productId)}
                productId={state.value}
                onOpenSelect={() => {
                  handleChange(0);
                }}
              />
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="spendThreshold">
        {({ state, handleChange, handleBlur }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Spend Threshold</InputLabel>
              <p className="text-sm text-zinc-500">
                This is the amount your customer has to spend to qualify for
                this reward
              </p>
              <InputContainer>
                <InputCurrency
                  placeholder="2000"
                  showPrefix
                  defaultValue={state.value}
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(Number(event.target.value))}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="quantity">
        {({ state, handleBlur, handleChange }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Quantity</InputLabel>
              <p className="text-sm text-zinc-500">
                This is how many of this reward qualifying customers will
                recieve
              </p>
              <InputContainer>
                <InputNumber
                  placeholder="1"
                  defaultValue={state.value}
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(Number(event.target.value))}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="price">
        {({ state, handleBlur, handleChange }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Price</InputLabel>
              <p className="text-sm text-zinc-500">
                This is how much your reward will cost. If you want qualifying
                customers to get the reward for free, enter '0' into this field
              </p>
              <InputContainer>
                <InputCurrency
                  defaultValue={state.value}
                  placeholder="0"
                  showPrefix
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(Number(event.target.value))}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <div className="flex justify-end gap-4">
        {mode === "update" && (
          <Button
            onClick={onDelete}
            color="red"
            disabled={isFormDisabled}
            loading={isDeleting}
            type="button"
          >
            Delete
          </Button>
        )}
        <Button disabled={isFormDisabled} loading={isSubmitting} type="submit">
          {mode === "create" && "Assign"}
          {mode === "update" && "Update"}
        </Button>
      </div>
    </form>
  );
};

const FieldError = ({ errorMessage }: { errorMessage: string | undefined }) => {
  if (!errorMessage) return null;
  return (
    <span role="alert" className="text-sm text-red-700">
      {errorMessage}
    </span>
  );
};

type PromotionRewardItemSelectProps = {
  productId: number | undefined;
  disabled?: boolean;
  onChange: (_id: number) => void;
  onOpenSelect: () => void;
};

const PromotionRewardItemSelect = ({
  productId,
  disabled,
  onOpenSelect,
  onChange,
}: PromotionRewardItemSelectProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebounceValue(searchValue, 500);
  const productList = useProductList(
    {
      limit: 10,
      sortBy: "name",
      sortDir: "asc",
      search: debouncedSearchValue,
    },
    { enabled: Boolean(debouncedSearchValue) },
  );

  const productQuery = useSupplierProduct(productId ?? 0, {
    enabled: Boolean(productId),
  });

  const product = productQuery.data;

  // We only want child products
  const products = (productList.data?.pages[0] ?? []).filter(
    (product) => product.ParentId,
  );

  if (product) {
    const [productParent, productName] = product.name?.split("•") ?? [
      "Parent",
      "Product",
    ];
    const image = product.imageUrl ?? product.Parent?.imageUrl ?? "";
    return (
      <div className="flex flex-col gap-1">
        <InputLabel>Choose a reward</InputLabel>
        <InputContainer className="flex justify-between">
          <div className="flex items-center gap-4">
            <ImageWithFallback
              img={image}
              fallback="product-badge"
              className="h-10 w-10"
            />
            <div className="flex flex-col">
              <span className="font-semibold">{productName}</span>
              <span className="text-sm text-zinc-600">{productParent}</span>
            </div>
          </div>
          <SmallButton
            disabled={disabled}
            onClick={onOpenSelect}
            color="secondary"
          >
            Change reward
          </SmallButton>
        </InputContainer>
      </div>
    );
  }

  return (
    <div className={twMerge("flex flex-col", products.length > 0 && "gap-4")}>
      <div className="flex flex-col gap-1">
        <InputLabel>Search for a product</InputLabel>
        <InputContainer className="w-full">
          <InputIcon icon="MAGNIFY" />
          <InputText
            disabled={disabled}
            placeholder="30L Keg"
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </InputContainer>
      </div>
      <div
        className={twMerge(
          "flex flex-col divide-y divide-zinc-300 rounded-md border-zinc-300",
          products.length > 0 && "border",
        )}
      >
        {products.map((product) => {
          const [productParent, productName] = product.name?.split("•") ?? [
            "Parent",
            "Product",
          ];
          const image = product.imageUrl ?? product.Parent?.imageUrl ?? "";
          return (
            <div
              key={product.id}
              className="flex items-center justify-between px-4 py-2"
            >
              <div className="flex items-center gap-4">
                <ImageWithFallback
                  img={image}
                  fallback="product-badge"
                  className="h-10 w-10"
                />
                <div className="flex flex-col">
                  <span className="font-semibold">{productName}</span>
                  <span className="text-sm text-zinc-600">{productParent}</span>
                </div>
              </div>
              <SmallButton
                color="secondary"
                onClick={() => onChange(product.id)}
              >
                Select reward
              </SmallButton>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PromotionRewardListItem = ({
  rewardItem,
}: {
  rewardItem: PromotionGetResponseType["PromotionRewardItems"][number];
}) => {
  const [dialogActive, setDialogActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const promotionRewardItemUpdateMutation = usePromotionRewardItemUpdate(
    rewardItem.id,
  );
  const promotionRewardItemDeleteMutation = usePromotionRewardItemDelete(
    rewardItem.id,
  );

  const handleDismiss = () => {
    setErrorMessage("");
    setDialogActive(false);
  };

  const handleUpdatePromotionRewardItemAssignment = (
    data: PromotionRewardItemFormSchemaType,
  ) => {
    setErrorMessage("");
    const spendThreshold = formatPriceInputToApi(data.spendThreshold);
    if (!spendThreshold) {
      setErrorMessage("Please set a valid spend threshold");
      return;
    }

    const price = formatPriceInputToApi(data.price);
    if (!price && price !== 0) {
      setErrorMessage("Set a valid price");
      return;
    }

    promotionRewardItemUpdateMutation.mutate(
      {
        quantity: data.quantity,
        spendThreshold,
        price,
        ProductId: data.productId,
      },
      {
        onSuccess: () => handleDismiss(),
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleDeletePromotionRewardItemAssignment = () => {
    setErrorMessage("");
    promotionRewardItemDeleteMutation.mutate(undefined, {
      onSuccess: () => handleDismiss(),
      onError: (error) => {
        const message = apiV2AssertError(error)?.response?.data.message ?? "";
        setErrorMessage(message);
      },
    });
  };

  const initialData: PromotionRewardItemFormSchemaType = {
    productId: rewardItem.Product.id,
    spendThreshold: Number(formatPriceApiToInput(rewardItem.spendThreshold)),
    quantity: rewardItem.quantity,
    price: Number(formatPriceApiToInput(rewardItem.price)),
  };

  return (
    <Fragment key={rewardItem.id}>
      <Card
        key={rewardItem.id}
        onClick={() => setDialogActive(true)}
        className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
      >
        <ImageWithFallback
          img={
            rewardItem.Product.imageUrl ??
            rewardItem.Product.Parent?.imageUrl ??
            ""
          }
          fallback="product-badge"
          className="h-10 w-10"
        />
        <div className="flex flex-1 flex-col">
          <span className="font-semibold">
            {rewardItem.Product.nameV2 ?? rewardItem.Product.name}
          </span>
          <span className="text-sm text-zinc-600">
            Spend <strong>{formatCurrency(rewardItem.spendThreshold)}</strong>{" "}
            and receive {rewardItem.quantity}{" "}
            <strong>
              {rewardItem.Product.nameV2 ?? rewardItem.Product.name}
            </strong>{" "}
            for <strong>{formatCurrency(rewardItem.price)}</strong>
          </span>
        </div>
        <div className="flex items-center">
          Edit
          <Icon icon="CHEVRON_RIGHT" size={0.9} className="text-zinc-400" />
        </div>
      </Card>
      <Dialog active={dialogActive} onDismiss={handleDismiss}>
        <DialogNavigation
          title="Update reward"
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={handleDismiss}
        />
        <DialogContent>
          <PromotionRewardItemForm
            mode="update"
            errorMessage={errorMessage}
            initialData={initialData}
            isDeleting={promotionRewardItemDeleteMutation.isPending}
            isSubmitting={promotionRewardItemUpdateMutation.isPending}
            onSubmit={handleUpdatePromotionRewardItemAssignment}
            onDelete={handleDeletePromotionRewardItemAssignment}
          />
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};
